import { useEffect, useState } from "react";
import { api, qs } from "@/lib/api";
import { Card, KpiCard, SectionTitle, NdBadge, TornaHome, VarianceIndicator } from "@/components/Kpi";
import BudgetGauge from "@/components/BudgetGauge";
import { COLORS, ComparisonChart, PieBreakdown } from "@/components/Charts";
import { PremiChart } from "@/components/PremiChart";
import { usePalette } from "@/lib/palette";
import { AzzeraFiltri, FilterBar, useFiltersMeta } from "@/components/Filters";
import GrigliaDashboard from "@/components/GrigliaDashboard";

const ANNO_DASHBOARD = 2026;
const sez = (nome, blocchi) => blocchi.map((b) => ({ ...b, sezione: nome }));
import { MESI, dateIt, eurShort, num, perc } from "@/lib/format";

/** Home: replica funzionale della pagina 1 della dashboard PDF DBE canale lungo 2026.
 *  Ogni grafico occupa una riga intera; 2025 in grigio sempre prima del 2026.
 *  Rosso = caricato, verde = liquidato. */
export default function HomePage({ slug, titolo }) {
  usePalette();
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState(slug ? { anno: ANNO_DASHBOARD, commerciale: slug }
    : { anno: ANNO_DASHBOARD });
  const [d, setD] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [reset, setReset] = useState(0);
  const [settoriDb, setSettoriDb] = useState(null);
  const anno = Number(filters.anno);

  useEffect(() => {
    if (slug) setFilters((f) => ({ ...f, commerciale: slug }));
  }, [slug]);

  useEffect(() => {
    api.get(`/dashboard/overview?${qs(filters)}`).then((r) => setD(r.data)).catch(() => setD(null));
    api.get(`/dashboard/agenzia?${qs(filters)}`)
      .then((r) => setSettoriDb(r.data)).catch(() => setSettoriDb(null));
  }, [filters]);

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);

  if (!d) return <div className="text-slate-500 text-sm" data-testid="home-loading">Caricamento…</div>;
  const t = d.totali;
  const ytd = d.progressivo_anno || d.totali;
  const serie = d.serie_mensile || [];
  const snap = d.snapshot;
  // settori/macro: dallo snapshot PDF quando disponibile, altrimenti dai dati reali
  const settoriView = snap?.settori?.length
    ? { rows: snap.settori, macro: snap.macro_liquidato,
        pratiche: snap.pratiche_liquidate, importo: snap.importo_liquidato }
    : settoriDb?.settori?.length
      ? { rows: settoriDb.settori.filter((s) => s.importo_liquidato)
            .map((s) => ({ settore: s.key, pratiche: s.pratiche_liquidate, importo: s.importo_liquidato })),
          macro: Object.fromEntries((settoriDb.macro || [])
            .filter((m) => m.importo_liquidato).map((m) => [m.key, m.importo_liquidato])),
          pratiche: settoriDb.totali?.pratiche_liquidate, importo: settoriDb.totali?.importo_liquidato }
      : null;

  // 2025 (grigio) sempre prima del 2026
  const pair = (base, colore) => [
    { key: `${base}_${anno - 1}`, name: `${anno - 1}`, color: COLORS.grigio },
    { key: `${base}_${anno}`, name: `${anno}`, color: colore },
  ];
  const CARICATO = COLORS.caricato;
  const LIQUIDATO = COLORS.liquidato;

  const CHARTS = [
    { id: "chart-medio-giornaliero-caricato", title: "Importo medio giornaliero caricato", base: "medio_giornaliero_caricato", color: CARICATO, span: 2 },
    { id: "chart-medio-giornaliero-liquidato", title: "Importo medio giornaliero liquidato", base: "medio_giornaliero_liquidato", color: LIQUIDATO, span: 2 },
    { id: "chart-pratiche-caricate", title: "Numero pratiche caricate", base: "pratiche", color: CARICATO },
    { id: "chart-importo-caricato", title: "Importo caricato", base: "caricato", color: CARICATO },
    { id: "chart-pratiche-liquidate", title: "Numero pratiche liquidate", base: "liquidate", color: LIQUIDATO },
    { id: "chart-importo-liquidato", title: "Importo liquidato", base: "liquidato", color: LIQUIDATO },
    { id: "chart-importo-medio-caricato", title: "Ticket medio caricato", base: "importo_medio_caricato", color: CARICATO, fmt: "int", span: 2 },
    { id: "chart-importo-medio-liquidato", title: "Ticket medio liquidato", base: "importo_medio_liquidato", color: LIQUIDATO, fmt: "int", span: 2 },
  ];

  const tabellaSettori = settoriView?.rows?.length ? (
    <Card className="overflow-hidden h-full" data-testid="home-settori-table">
      <div className="p-6 pb-3 uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
        Distribuzione per settore · pratiche liquidate
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
            <th className="text-left px-6 py-3">Settore</th>
            <th className="text-right px-6 py-3">N° pratiche</th>
            <th className="text-right px-6 py-3">Importo finanziato</th>
            <th className="text-right px-6 py-3">% sul totale</th>
          </tr></thead>
          <tbody>
            {settoriView.rows.map((s) => (
              <tr key={s.settore} data-testid={`home-settore-${s.settore}`} className="border-t border-slate-100">
                <td className="px-6 py-2.5 font-medium">{s.settore}</td>
                <td className="px-6 py-2.5 text-right font-mono">{num(s.pratiche)}</td>
                <td className="px-6 py-2.5 text-right font-mono">{eurShort(s.importo)}</td>
                <td className="px-6 py-2.5 text-right font-mono">{perc(s.importo / settoriView.importo * 100)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <td className="px-6 py-3">Totale complessivo</td>
              <td className="px-6 py-3 text-right font-mono">{num(settoriView.pratiche)}</td>
              <td className="px-6 py-3 text-right font-mono">{eurShort(settoriView.importo)}</td>
              <td className="px-6 py-3 text-right font-mono">100,00%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  ) : null;

  const boxFda = (
    <Card className="p-6 h-full flex flex-col justify-center" data-testid="fda-univoche-box">
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
        FDA attive univoche · da inizio {anno}
      </div>
      <div className="font-mono text-4xl font-semibold mt-3 text-emerald-600">{num(d.fda?.anno_univoche)}</div>
      <div className="text-sm text-slate-500 mt-2">dealer distinti con almeno una pratica caricata nell'anno</div>
      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{anno - 1} · alla stessa data</div>
        <div className="font-mono text-2xl font-semibold text-slate-500">{num(d.fda?.anno_precedente_univoche)}</div>
        <div className="mt-2"><VarianceIndicator value={d.fda?.anno_precedente_univoche
          ? ((d.fda.anno_univoche - d.fda.anno_precedente_univoche) / d.fda.anno_precedente_univoche) * 100 : null} /></div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100" data-testid="fda-nuove-convenzioni">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Nuove convenzioni {anno}</div>
        <div className="font-mono text-2xl font-semibold text-sky-700">{num(d.fda?.nuove_convenzioni)}</div>
        <div className="text-xs text-slate-500 mt-1">dealer con data attivazione nel {anno}, anche senza pratiche</div>
      </div>
    </Card>
  );

  const meseLbl = d.proiezione_mese?.mese_precedente
    ? MESI[d.proiezione_mese.mese - 1].toLowerCase() : "mese";
  const notaMese = d.proiezione_mese?.mese_precedente
    ? `giorni lavorati di ${MESI[(d.calendario?.mese || 1) - 1].toLowerCase()} non ancora inseriti` : undefined;
  return (
    <div data-testid="home-page">
      <TornaHome testId="btn-home-lungo-torna-home" />
      <SectionTitle sub={`Dashboard ${anno} · aggiornata al ${dateIt(d.ultima_data_pratiche || snap?.data_aggiornamento)} · ${d.calendario.giorni_lavorativi_anno_trascorsi}/${d.calendario.giorni_lavorativi_anno} giorni lavorativi · vista ${filters.dal || filters.al ? `dal ${filters.dal ? dateIt(filters.dal) : "inizio"} al ${filters.al ? dateIt(filters.al) : "oggi"}` : `mese ${filters.mese ? String(filters.mese).padStart(2, "0") : "tutti"}`}`}
        right={
          <div className="flex flex-wrap gap-2">
            <a href={`/api/report/dashboard.pdf?${qs(filters)}`}
              data-testid="btn-report-pdf"
              className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
              Scarica report PDF
            </a>
            <a href={`/api/report/home-completo.pdf?${qs(filters)}`} target="_blank" rel="noreferrer"
              data-testid="btn-report-completo-pdf"
              className="px-4 py-2 rounded-md border border-[#0018A8] text-[#0018A8] text-sm hover:bg-slate-100 transition-colors duration-200">
              Report completo PDF
            </a>
          </div>
        }>
        {titolo || "DASHBOARD LUNGO DBE - TOTALE"}
      </SectionTitle>
      <div className="flex flex-wrap items-center gap-3">
        <FilterBar key={reset} meta={meta} value={filters} onChange={setFilters} dealers={dealers}
          fields={["mese", "intervallo", "macro", "prodotto", "dealer"]} />
        <AzzeraFiltri onClick={() => {
          setFilters({ anno: ANNO_DASHBOARD, ...(slug ? { commerciale: slug } : {}) });
          setReset((v) => v + 1);
        }} />
      </div>

      {(d.ultima_data_pratiche || snap?.data_aggiornamento) && (
        <Card className="p-4 mb-6 border-sky-200 bg-sky-50/60" data-testid="missing-fields-banner">
          <div className="text-sm text-sky-900">
            Dati aggiornati al <b>{dateIt(d.ultima_data_pratiche || snap?.data_aggiornamento)}</b>
          </div>
        </Card>
      )}

      <GrigliaDashboard pagina={slug ? "commerciale" : "home"} filtri={filters} blocchi={[
        ...sez("INDICATORI", [
        { id: "kpi-pratiche-caricate", label: "Pratiche caricate", span: 1,
          node: <KpiCard testId="kpi-pratiche-caricate" label="Pratiche caricate" value={t.pratiche_caricate} accent="caricato" /> },
        { id: "kpi-importo-caricato", label: "Importo caricato", span: 1,
          node: <KpiCard testId="kpi-importo-caricato" label="Importo caricato" value={t.importo_caricato} kind="eurShort" accent="caricato" note={t.perimetro_importi} /> },
        { id: "kpi-pratiche-liquidate", label: "Pratiche liquidate", span: 1,
          node: <KpiCard testId="kpi-pratiche-liquidate" label="Pratiche liquidate" value={t.pratiche_liquidate} accent="liquidato" /> },
        { id: "kpi-importo-liquidato", label: "Importo liquidato", span: 1,
          node: <KpiCard testId="kpi-importo-liquidato" label="Importo liquidato" value={t.importo_liquidato} kind="eurShort" accent="liquidato" note={t.perimetro_importi} /> },
        { id: "kpi-budget-caricato", label: "% budget caricato", span: 1,
          node: <KpiCard testId="kpi-budget-caricato" label="% raggiungimento budget caricato" value={d.budget_caricato.raggiungimento_perc} kind="perc" accent="caricato" sub={`Budget ${eurShort(d.budget_caricato.budget_importo)}`} /> },
        { id: "kpi-previsione-anno-caricato", label: "Previsione anno caricato", span: 1,
          node: <KpiCard testId="kpi-previsione-anno-caricato" label="Previsione chiusura anno · caricato" value={d.budget_caricato.proiezione_importo} kind="eurShort" accent="caricato" variance={d.budget_caricato.scostamento_perc} sub="scostamento vs budget" /> },
        { id: "kpi-budget-liquidato", label: "% budget liquidato", span: 1,
          node: <KpiCard testId="kpi-budget-liquidato" label="% raggiungimento budget liquidato" value={d.budget_liquidato.raggiungimento_perc} kind="perc" accent="liquidato" sub={`Budget ${eurShort(d.budget_liquidato.budget_importo)}`} /> },
        { id: "kpi-previsione-anno-liquidato", label: "Previsione anno liquidato", span: 1,
          node: <KpiCard testId="kpi-previsione-anno-liquidato" label="Previsione chiusura anno · liquidato" value={d.budget_liquidato.proiezione_importo} kind="eurShort" accent="liquidato" variance={d.budget_liquidato.scostamento_perc} sub="scostamento vs budget" /> },
        { id: "kpi-previsione-mese-pratiche", label: "Previsione mese pratiche", span: 1,
          node: <KpiCard testId="kpi-previsione-mese-pratiche" label={`Previsione chiusura ${meseLbl} · pratiche caricate`} value={d.proiezione_mese.pratiche} note={notaMese} accent="caricato" /> },
        { id: "kpi-previsione-mese-importo", label: "Previsione mese importo", span: 1,
          node: <KpiCard testId="kpi-previsione-mese-importo" label={`Previsione chiusura ${meseLbl} · importo caricato`} value={d.proiezione_mese.importo} note={notaMese} kind="eurShort" accent="caricato" /> },
        { id: "kpi-previsione-mese-liquidate", label: "Previsione mese liquidate", span: 1,
          node: <KpiCard testId="kpi-previsione-mese-liquidate" label={`Previsione chiusura ${meseLbl} · pratiche liquidate`} value={d.proiezione_mese.pratiche_liquidate} note={notaMese} accent="liquidato" /> },
        { id: "kpi-previsione-mese-liquidato", label: "Previsione mese liquidato", span: 1,
          node: <KpiCard testId="kpi-previsione-mese-liquidato" label={`Previsione chiusura ${meseLbl} · importo liquidato`} value={d.proiezione_mese.importo_liquidato} note={notaMese} kind="eurShort" accent="liquidato" /> },
        { id: "kpi-accettate-da-liquidare", label: "Accettato da liquidare", span: 1,
          node: <KpiCard testId="kpi-accettate-da-liquidare" label="Accettato da liquidare" value={ytd.importo_accettato_da_liquidare} kind="eurShort" accent="warning"
            sub={`AUTO ${eurShort(ytd.importo_accettato_da_liquidare_auto)}${ytd.pratiche_accettate_da_liquidare_auto ? ` (${num(ytd.pratiche_accettate_da_liquidare_auto)})` : ""} · NON AUTO ${eurShort(ytd.importo_accettato_da_liquidare_pos)}${ytd.pratiche_accettate_da_liquidare_pos ? ` (${num(ytd.pratiche_accettate_da_liquidare_pos)})` : ""}`}
            note={`${num(ytd.pratiche_accettate_da_liquidare_pdf ?? ytd.pratiche_accettate_da_liquidare)} pratiche${ytd.accettato_da_liquidare_fonte ? ` · file del ${dateIt(ytd.accettato_da_liquidare_fonte.aggiornato_il)}` : ""}`} /> },
        ]),
        ...sez("LIQUIDATO AUTO vs NON AUTO", settoriView?.rows?.length ? [
          { id: "chart-macro-liquidato", label: "Torta AUTO/NON AUTO", span: 2,
            node: <PieBreakdown testId="chart-macro-liquidato" height={300} title="Importo liquidato · AUTO vs NON AUTO"
              data={Object.entries(settoriView.macro || {}).map(([k, v]) => ({ name: k, value: v, color: k === "AUTO" ? "#0284C7" : "#7DD3FC" }))} /> },
          { id: "home-settori-table", label: "Tabella settori", span: 2, node: tabellaSettori },
        ] : []),
        ...sez("GRAFICI", [
        ...CHARTS.flatMap((c) => [{
          id: c.id, label: c.title, span: c.span || 4,
          node: <ComparisonChart testId={c.id} height={c.span === 2 ? 520 : 320} labelFormat={c.fmt}
            orizzontale={c.span === 2}
            title={`${c.title} · ${anno - 1} vs ${anno}`} data={serie} keys={pair(c.base, c.color)} />,
        }, ...(c.id === "chart-importo-liquidato" ? [{
          id: "chart-premi-liquidati", label: "Premi assicurativi liquidati", span: 4,
          node: <PremiChart testId="chart-premi-liquidati" anno={anno} data={d.premi_mensili || []} />,
        }] : [])]),
        { id: "chart-fda-attive", label: "FDA attive", span: 3,
          node: <ComparisonChart testId="chart-fda-attive" height={320} type="line"
            title={`FDA attive per mese · ${anno - 1} vs ${anno}`} data={d.fda?.serie || []}
            keys={[{ key: `fda_${anno - 1}`, name: `${anno - 1}`, color: COLORS.grigio },
              { key: `fda_${anno}`, name: `${anno}`, color: COLORS.liquidato }]}
            emptyNote="nessun dealer con pratiche nel periodo" /> },
        { id: "fda-univoche-box", label: "FDA univoche", span: 1, node: boxFda },
        ]),
        ...sez("TACHIMETRI", [
        ...["CARICATO", "LIQUIDATO", "PREMI"].map((gruppo) => {
          const gruppi = (d.budgets || []).filter((b) => b.tipo_kpi === gruppo);
          if (!gruppi.length) return null;
          const card = (b) => (
            <BudgetGauge key={b.categoria} testId={`gauge-${b.tipo_kpi}-${b.categoria}`}
              titolo={b.etichetta || `Budget ${b.tipo_kpi.toLowerCase()} · ${b.categoria}`}
              sottotitolo={b.tipo_kpi === "PREMI" ? "previsione chiusura anno vs budget" : "previsione chiusura anno vs budget · Progetto e Posa a consuntivo"}
              budget={b.budget_importo} proiezione={b.proiezione_importo}
              scostamentoPerc={b.scostamento_perc} tipo={b.tipo_kpi}
              colore={b.tipo_kpi === "LIQUIDATO" ? COLORS.liquidato : b.tipo_kpi === "PREMI" ? "#0284C7" : COLORS.caricato} />
          );
          return {
            id: `gauges-${gruppo.toLowerCase()}`, label: `Tachimetri ${gruppo.toLowerCase()}`, span: 4,
            node: (
              <div className={`grid gap-6 ${gruppo === "PREMI"
                ? "grid-cols-1 md:grid-cols-2 md:px-[16.6%]" : "grid-cols-1 md:grid-cols-3"}`}
                data-testid={`gauges-${gruppo.toLowerCase()}`}>
                {gruppi.map(card)}
              </div>
            ),
          };
        }).filter(Boolean),
        ]),
      ]} />
    </div>
  );
}
